import React, { useEffect, useState } from "react";
import { PageHeader, TopTabs } from "@/components/PageHeader";
import { NotInvoicedReport } from "@/components/NotInvoicedReport";
import { PaymentsReport } from "@/components/PaymentsReport";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  ["server_name", "Server"], ["jobs_assigned", "Jobs"], ["jobs_served", "Served"], ["jobs_non_served", "Non-Served"], ["jobs_open", "Open"],
  ["attempts", "Attempts"], ["attempts_with_photos", "w/ Photos"], ["attempts_per_served_job", "Attempts / Serve"], ["avg_days_to_serve", "Avg Days to Serve"], ["success_rate", "Success %"],
];
const fmt = (k, v) => v === null || v === undefined ? "—" : k === "success_rate" ? `${v}%` : v;

export default function Reports() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") === "not-invoiced" ? "Not Invoiced" : "Server Performance");
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (r = range) => {
    setLoading(true);
    try {
      const params = {}; if (r.date_from) params.date_from = r.date_from; if (r.date_to) params.date_to = r.date_to;
      const res = await api.get("/reports/servers", { params }); setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load report"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const csvUrl = `${api.defaults.baseURL}/reports/servers?fmt=csv${range.date_from ? `&date_from=${range.date_from}` : ""}${range.date_to ? `&date_to=${range.date_to}` : ""}`;
  const preset = (days) => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - days); const r = { date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) }; setRange(r); load(r); };

  return (
    <>
      <TopTabs tabs={[{ label: "Server Performance" }, { label: "Not Invoiced" }, { label: "Payments Received" }]} value={tab} onChange={setTab} />
      {tab === "Payments Received" ? (
        <>
          <PageHeader title="Payments Received" description="Everything paid in QuickBooks this month, grouped by company, with the Simply Served jobs each payment covered." />
          <PaymentsReport />
        </>
      ) : tab === "Not Invoiced" ? (
        <>
          <PageHeader title="Jobs Not Invoiced" description="Completed jobs that still need an invoice, grouped by client and then by case. Generate the PDF and it's emailed as a secure download link to your accounting address." />
          <NotInvoicedReport />
        </>
      ) : (
      <>
      <PageHeader title="Server Performance" description="Attempts per job, days to serve and success rate per process server. Filter by the date jobs were received."
        actions={<a href={csvUrl} data-testid="report-csv-link"><Button variant="outline" size="sm"><Download className="h-3.5 w-3.5 mr-1" />Export CSV</Button></a>} />
      <div className="p-4 sm:p-7 space-y-4" data-testid="reports-page">
        <div className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded p-4">
          <div><div className="sm-label mb-1">From</div><Input type="date" className="h-9 w-40" data-testid="report-from" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} /></div>
          <div><div className="sm-label mb-1">To</div><Input type="date" className="h-9 w-40" data-testid="report-to" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} /></div>
          <Button size="sm" className="sm-green" onClick={() => load()} data-testid="report-apply" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
          <div className="flex gap-2 text-xs ml-auto">
            {[["30 days", 30], ["90 days", 90], ["12 months", 365]].map(([l, d]) => <button key={d} type="button" className="sm-link" onClick={() => preset(d)} data-testid={`report-preset-${d}`}>{l}</button>)}
            <button type="button" className="sm-link" onClick={() => { const r = { date_from: "", date_to: "" }; setRange(r); load(r); }} data-testid="report-preset-all">All time</button>
          </div>
        </div>
        {data && (
          <div className="text-xs text-slate-500" data-testid="report-summary">{data.total_jobs} job{data.total_jobs === 1 ? "" : "s"} · {data.total_attempts} attempt{data.total_attempts === 1 ? "" : "s"}{data.date_from || data.date_to ? ` · ${data.date_from || "…"} → ${data.date_to || "…"}` : " · all time"}</div>
        )}
        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-table">
            <thead><tr className="text-left border-b border-slate-200">{COLS.map(([k, l]) => <th key={k} className={`sm-label px-3 py-2 font-medium ${k !== "server_name" ? "text-right" : ""}`}>{l}</th>)}</tr></thead>
            <tbody>
              {data?.rows?.length === 0 && <tr><td colSpan={COLS.length} className="px-3 py-6 text-slate-400 text-center">No servers or jobs in this range.</td></tr>}
              {data?.rows?.map((r) => (
                <tr key={r.server_id || "unassigned"} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`report-row-${r.server_id || "unassigned"}`}>
                  {COLS.map(([k]) => (
                    <td key={k} className={`px-3 py-2 ${k !== "server_name" ? "text-right tabular-nums" : "font-medium"}`}>
                      {k === "server_name" ? <>{r.server_name}{!r.active && <span className="ml-2 text-[10px] text-slate-400">inactive</span>}</>
                        : k === "success_rate" && r.success_rate !== null ? <span className={r.success_rate >= 80 ? "text-emerald-700" : r.success_rate >= 50 ? "text-amber-700" : "text-rose-700"}>{fmt(k, r[k])}</span>
                        : fmt(k, r[k])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </>
  );
}
